import { Button, InputNumber, Slider } from 'antd';
import { useTranslation } from 'react-i18next';

/**
 * A 0–100 % likelihood that STARTS UNSET (D-expectancies-2026-10-07: "not asked" is absence, never
 * 0). A Form.Item-compatible control: `value` undefined/null means unanswered, the slider handle then
 * rests at 0 but dimmed with a "not set" hint, and nothing is sent until the person moves it or types
 * a number. A Clear button returns it to unanswered.
 */
export function ExpositionLikelihoodField({
  value,
  onChange,
  disabled,
  testId,
}: {
  value?: number | null;
  onChange?: (v: number | null) => void;
  disabled?: boolean;
  testId: string;
}) {
  const { t } = useTranslation();
  const unset = value === undefined || value === null;
  return (
    <div data-testid={testId} data-unset={unset ? 'true' : 'false'}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Slider
          style={{ flex: 1, minWidth: 160, opacity: unset ? 0.45 : 1 }}
          min={0}
          max={100}
          value={unset ? 0 : (value as number)}
          disabled={disabled}
          onChange={(v) => onChange?.(v)}
          tooltip={{ open: false }}
          data-testid={`${testId}-slider`}
        />
        <InputNumber
          style={{ width: 90 }}
          min={0}
          max={100}
          precision={0}
          step={5}
          value={unset ? null : (value as number)}
          disabled={disabled}
          placeholder="—"
          formatter={(v) =>
            v === undefined || v === null || String(v) === '' ? '' : `${v} %`
          }
          parser={(v) => Number((v ?? '').replace(/[^0-9]/g, ''))}
          onChange={(v) => onChange?.(typeof v === 'number' ? v : null)}
          data-testid={`${testId}-input`}
        />
        <Button
          size="small"
          type="link"
          disabled={disabled || unset}
          onClick={() => onChange?.(null)}
          data-testid={`${testId}-clear`}
        >
          {t('shared.exposure.likelihoodClear')}
        </Button>
      </div>
      {unset ? (
        <div style={{ fontSize: 12, opacity: 0.65 }}>
          {t('shared.exposure.likelihoodNotSet')}
        </div>
      ) : null}
    </div>
  );
}
